import { useMemo } from 'react'
import { rangeToFilters } from '../components/analytics/FilterBar'
import { useBreakdown, type AnalyticsFilters } from './useAnalytics'

interface SeedVisit {
  seed: string
  visits: number
  lastVisit: string
  countries: string[]
}

interface ClientActivity {
  seeds: SeedVisit[]
}

/**
 * A client's activity per seed.
 *
 * Used to fetch up to 10,000 raw events and group them in the browser, which
 * truncated silently past that point and shipped megabytes to do arithmetic the
 * database can do. Now two server-side breakdowns.
 */
export function useClientActivity(email: string | null) {
  const filters = useMemo<AnalyticsFilters>(() => {
    // A year back: this is "where has this person been", not a live feed.
    const { from, to } = rangeToFilters(365)
    return { from, to, email: email ? [email] : undefined }
  }, [email])

  const enabled = !!email
  const bySeed = useBreakdown(filters, 'seed', undefined, 100, enabled)
  const byCountry = useBreakdown(filters, 'seed', 'country', 300, enabled)

  const activity = useMemo<ClientActivity | null>(() => {
    if (!email) return null

    const countriesBySeed = new Map<string, string[]>()
    for (const row of byCountry.rows) {
      if (row.key === null || row.subKey === null || row.subKey === '') continue
      const list = countriesBySeed.get(String(row.key)) ?? []
      list.push(String(row.subKey))
      countriesBySeed.set(String(row.key), list)
    }

    const seeds: SeedVisit[] = bySeed.rows
      .filter(row => row.key !== null)
      .map(row => ({
        seed: String(row.key),
        visits: row.events,
        lastVisit: new Date(Number(row.lastSeen)).toISOString(),
        // Breakdown rows arrive ordered by event count, so a seed's busiest
        // country is already first.
        countries: countriesBySeed.get(String(row.key)) ?? [],
      }))
      .sort((a, b) => b.lastVisit.localeCompare(a.lastVisit))

    return { seeds }
  }, [email, bySeed.rows, byCountry.rows])

  return { activity, loading: bySeed.loading || byCountry.loading }
}
